import { RepeatableList } from "../components/RepeatableList";
import { TextField } from "../components/TextField";

// A schema-agnostic editor over the content JSON: strings become text inputs,
// arrays become add/remove/reorder lists, objects become labelled groups.
// This handles every page's content (and structural edits) without a bespoke
// editor per page. New array items are seeded from the shape of an existing
// item so cards/sections keep the right fields.

type Json = unknown;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function humanize(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function blankLike(sample: unknown): unknown {
  if (typeof sample === "string") return "";
  if (typeof sample === "number") return 0;
  if (typeof sample === "boolean") return false;
  if (Array.isArray(sample)) return sample.length ? [blankLike(sample[0])] : [];
  if (isPlainObject(sample)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(sample)) out[key] = blankLike(sample[key]);
    return out;
  }
  return "";
}

export function JsonEditor({
  label,
  value,
  onChange,
}: {
  label?: string;
  value: Json;
  onChange: (value: Json) => void;
}) {
  if (typeof value === "string") {
    return <TextField label={label ?? ""} value={value} onChange={onChange} multiline={value.length > 60} />;
  }

  if (typeof value === "number") {
    return (
      <TextField
        label={label ?? ""}
        value={String(value)}
        onChange={(v) => onChange(v.trim() === "" ? 0 : Number(v))}
      />
    );
  }

  if (typeof value === "boolean") {
    return (
      <label className="field-check">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
        <span className="field-label">{label}</span>
      </label>
    );
  }

  if (Array.isArray(value)) {
    const sample = value[0];
    return (
      <RepeatableList<Json>
        label={label ?? ""}
        items={value}
        onChange={onChange}
        createItem={() => blankLike(sample ?? "")}
        renderItem={(item, onItemChange) => <JsonEditor value={item} onChange={onItemChange} />}
      />
    );
  }

  if (isPlainObject(value)) {
    return (
      <fieldset className="json-object">
        {label && <legend>{label}</legend>}
        {Object.keys(value).map((key) => (
          <JsonEditor
            key={key}
            label={humanize(key)}
            value={value[key]}
            onChange={(v) => onChange({ ...value, [key]: v })}
          />
        ))}
      </fieldset>
    );
  }

  return null;
}
